/*
  Wishlist and compare lists.

  Both lists live in localStorage, so a shopper who is not signed in keeps
  them between visits. This one file does three jobs:

    1. Header counts. Every [data-kv-wishlist-count] and
       [data-kv-compare-count] is refilled the moment a list changes.
    2. Buttons. The heart and the compare toggle on product cards, and the
       two links on the product page, add to a list on the first click and
       open the list page on the next one.
    3. List pages. The wishlist and compare sections cannot read the browser
       from Liquid, so the script fetches each saved product through the
       Section Rendering API (/products/<handle>?section_id=...) and drops the
       returned markup into the page. The markup is the same Liquid the rest
       of the theme uses, so a saved product looks like it does anywhere else.

  Loaded on every page by the kv-wishlist-assets snippet. Both list sections
  render that snippet too, so the guard below keeps a second copy inert.
*/
(function () {
  'use strict';

  if (window.KvLists) return;

  var STORAGE = { wishlist: 'kv-wishlist', compare: 'kv-compare' };
  var CHANGE_EVENT = 'kv:lists:change';

  var script = document.currentScript || document.querySelector('script[data-kv-lists]');
  var config = (script && script.dataset) || {};

  /*
    The storefront root is '/' on a plain store but '/en' or '/en-ca' once the
    store serves more than one market or language, and it arrives without a
    trailing slash. Paths are built by joining onto it, so it is normalised
    once here rather than at every join.
  */
  function withSlash(value) {
    return String(value || '/').replace(/\/*$/, '/');
  }

  var rootUrl = withSlash(config.root);
  var labels = {
    compareAdd: config.labelCompareAdd || 'Add to compare',
    compareAdded: config.labelCompareAdded || 'Compare products',
    wishlistAdded: config.labelWishlistAdded || 'Browse wishlist'
  };

  /* Storage ------------------------------------------------------------- */

  function read(name) {
    try {
      var raw = window.localStorage.getItem(STORAGE[name]);
      var parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (handle) {
        return typeof handle === 'string' && handle !== '';
      });
    } catch (error) {
      return [];
    }
  }

  function write(name, handles) {
    try {
      window.localStorage.setItem(STORAGE[name], JSON.stringify(handles));
    } catch (error) {
      /* Private mode or a full store: the page keeps working, nothing persists. */
    }
  }

  function notify(name, source) {
    updateCounts();
    /*
      A cancelled checkbox click is reverted by the browser after the event
      has finished, so the pressed state is applied once that has happened.
    */
    window.setTimeout(function () {
      syncButtons();
    }, 0);
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { list: name, handles: read(name), source: source || '' }
      })
    );
  }

  function makeList(name) {
    return {
      name: name,
      get: function () {
        return read(name);
      },
      has: function (handle) {
        return read(name).indexOf(handle) !== -1;
      },
      count: function () {
        return read(name).length;
      },
      add: function (handle, source) {
        if (!handle) return;
        var handles = read(name);
        if (handles.indexOf(handle) !== -1) return;
        handles.push(handle);
        write(name, handles);
        notify(name, source);
      },
      remove: function (handle, source) {
        this.removeMany([handle], source);
      },
      removeMany: function (list, source) {
        var handles = read(name).filter(function (handle) {
          return list.indexOf(handle) === -1;
        });
        write(name, handles);
        notify(name, source);
      },
      set: function (handles, source) {
        write(name, handles);
        notify(name, source);
      }
    };
  }

  var lists = {
    wishlist: makeList('wishlist'),
    compare: makeList('compare')
  };

  /* Urls ---------------------------------------------------------------- */

  function pageUrl(name) {
    var counter = document.querySelector('[data-kv-' + name + '-count]');
    var link = counter && counter.closest('a[href]');
    return (link && link.getAttribute('href')) || rootUrl + 'pages/' + name;
  }

  function handleFromUrl(url) {
    var match = /\/products\/([^/?#]+)/.exec(url || '');
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      return match[1];
    }
  }

  function cardHandle(card) {
    if (!card) return '';
    var link = card.querySelector('a[href*="/products/"]');
    return handleFromUrl(link && link.getAttribute('href'));
  }

  /* Header counts ------------------------------------------------------- */

  function updateCounts() {
    Object.keys(lists).forEach(function (name) {
      var count = String(lists[name].count());
      var nodes = document.querySelectorAll('[data-kv-' + name + '-count]');
      Array.prototype.forEach.call(nodes, function (node) {
        if (node.textContent !== count) node.textContent = count;
      });
    });
  }

  /* Product cards and the product page ---------------------------------- */

  function setPressed(input, added) {
    if (!input) return;
    input.checked = added;
    var label = input.closest('label');
    if (label) label.classList.toggle('is-added', added);
  }

  function syncCards(scope) {
    var wishlist = lists.wishlist.get();
    var compare = lists.compare.get();
    var cards = (scope || document).querySelectorAll('.kv-card');
    Array.prototype.forEach.call(cards, function (card) {
      var handle = cardHandle(card);
      if (!handle) return;
      setPressed(card.querySelector('.kv-card__wishlist-state'), wishlist.indexOf(handle) !== -1);
      setPressed(card.querySelector('.kv-card__compare-state'), compare.indexOf(handle) !== -1);
    });
  }

  /*
    The product page block renders the compare link and then the wishlist
    link. Only the wishlist one carries the heart icon, which tells them apart
    without depending on their order or their text.
  */
  function productActionName(action) {
    return action.querySelector('.icon-heart') ? 'wishlist' : 'compare';
  }

  function productActionText(action) {
    var spans = action.querySelectorAll(':scope > span');
    for (var i = spans.length - 1; i >= 0; i -= 1) {
      if (!spans[i].classList.contains('kv-product__action-icon')) return spans[i];
    }
    return null;
  }

  function syncProductActions() {
    var handle = handleFromUrl(window.location.pathname);
    if (!handle) return;
    var actions = document.querySelectorAll('.kv-product__actions .kv-product__action');
    Array.prototype.forEach.call(actions, function (action) {
      var name = productActionName(action);
      var added = lists[name].has(handle);
      action.classList.toggle('is-added', added);
      var text = productActionText(action);
      if (!text) return;
      if (!action.dataset.kvLabel) action.dataset.kvLabel = text.textContent.trim();
      var addedLabel = name === 'wishlist' ? labels.wishlistAdded : labels.compareAdded;
      text.textContent = added ? addedLabel : action.dataset.kvLabel;
    });
  }

  function syncButtons(scope) {
    syncCards(scope);
    syncProductActions();
  }

  function onCardToggle(toggle, event) {
    var name = null;
    if (toggle.classList.contains('kv-card__wishlist')) {
      name = 'wishlist';
    } else if (toggle.querySelector('.kv-card__compare-state')) {
      name = 'compare';
    }
    if (!name) return;

    event.preventDefault();
    var handle = cardHandle(toggle.closest('.kv-card'));
    if (!handle) return;

    if (lists[name].has(handle)) {
      window.location.href = pageUrl(name);
      return;
    }
    lists[name].add(handle, 'card');
    tooltip.refresh();
  }

  function onProductAction(action, event) {
    var handle = handleFromUrl(window.location.pathname);
    if (!handle) return;
    var name = productActionName(action);

    if (lists[name].has(handle)) {
      /* A link already points at the list page; a button has to be sent there. */
      if (action.tagName !== 'A') {
        event.preventDefault();
        window.location.href = pageUrl(name);
      }
      return;
    }
    event.preventDefault();
    lists[name].add(handle, 'product');
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('.kv-card__wishlist, .kv-card__tool');
    if (toggle) {
      onCardToggle(toggle, event);
      return;
    }
    var action = event.target.closest('.kv-product__actions .kv-product__action');
    if (action) onProductAction(action, event);
  });

  /* Tooltip on the card compare toggle ---------------------------------- */

  var tooltip = (function () {
    var element = null;
    var anchor = null;

    function build() {
      element = document.createElement('div');
      element.className = 'kv-tooltip';
      element.setAttribute('role', 'tooltip');
      element.innerHTML = '<span class="kv-tooltip__inner"></span><span class="kv-tooltip__arrow"></span>';
      document.body.appendChild(element);
      return element;
    }

    function textFor(target) {
      var handle = cardHandle(target.closest('.kv-card'));
      return lists.compare.has(handle) ? labels.compareAdded : labels.compareAdd;
    }

    function place() {
      if (!anchor || !element) return;
      var rect = anchor.getBoundingClientRect();
      element.style.left = rect.left + rect.width / 2 + 'px';
      element.style.top = rect.top + 'px';
    }

    function show(target) {
      anchor = target;
      var node = element || build();
      node.querySelector('.kv-tooltip__inner').textContent = textFor(target);
      node.classList.add('is-visible');
      place();
    }

    function hide() {
      anchor = null;
      if (element) element.classList.remove('is-visible');
    }

    function refresh() {
      if (anchor) show(anchor);
    }

    return { show: show, hide: hide, refresh: refresh, place: place };
  })();

  function compareToolFrom(node) {
    if (!node || !node.closest) return null;
    var tool = node.closest('.kv-card__tool');
    return tool && tool.querySelector('.kv-card__compare-state') ? tool : null;
  }

  document.addEventListener('mouseover', function (event) {
    var tool = compareToolFrom(event.target);
    if (tool) tooltip.show(tool);
  });
  document.addEventListener('mouseout', function (event) {
    var tool = compareToolFrom(event.target);
    if (tool && !tool.contains(event.relatedTarget)) tooltip.hide();
  });
  document.addEventListener('focusin', function (event) {
    var tool = compareToolFrom(event.target);
    if (tool) tooltip.show(tool);
  });
  document.addEventListener('focusout', function (event) {
    if (compareToolFrom(event.target)) tooltip.hide();
  });
  window.addEventListener('scroll', tooltip.place, true);
  window.addEventListener('resize', tooltip.place);

  /* Section Rendering API ----------------------------------------------- */

  var parser = new DOMParser();

  function fetchFragment(page, handle, kind) {
    var base = withSlash(page.dataset.root || rootUrl);
    var url = base + 'products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(page.dataset.sectionId);

    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (response.status === 404) return { handle: handle, missing: true };
        if (!response.ok) return { handle: handle, fragment: null };
        return response.text().then(function (html) {
          var doc = parser.parseFromString(html, 'text/html');
          var fragment = doc.querySelector('[data-kv-fragment="' + kind + '"]');
          /*
            A product that no longer exists answers with the not-found page,
            and on that page the section renders its page markup instead of
            a fragment. That is the sign the handle is dead.
          */
          if (!fragment && doc.querySelector('[data-kv-wishlist-page], [data-kv-compare-page]')) {
            return { handle: handle, missing: true };
          }
          if (!fragment) return { handle: handle, fragment: null };
          /*
            The card brings its own stylesheet links along. They stay: a
            stylesheet the page already has costs nothing, and one it does
            not have yet is exactly what the card needs.
          */
          return { handle: handle, fragment: document.importNode(fragment, true) };
        });
      })
      .catch(function () {
        return { handle: handle, fragment: null };
      });
  }

  /*
    Four states, and the difference between the last two matters: "empty"
    means the shopper saved nothing, "error" means they saved something the
    store would not hand back. Telling someone their list is empty when the
    network dropped would lose their list in front of them.
  */
  function setPageState(page, state) {
    page.classList.toggle('is-loading', state === 'loading');
    page.classList.toggle('is-empty', state === 'empty');
    page.classList.toggle('is-error', state === 'error');
    page.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    var loading = page.querySelector('[data-kv-loading]');
    var empty = page.querySelector('[data-kv-empty]');
    var error = page.querySelector('[data-kv-error]');
    var content = page.querySelector('[data-kv-content]');
    if (loading) loading.hidden = state !== 'loading';
    if (empty) empty.hidden = state !== 'empty';
    if (error) error.hidden = state !== 'error';
    if (content) content.hidden = state !== 'ready';
  }

  /* Wishlist page ------------------------------------------------------- */

  function initWishlistPage(page) {
    var grid = page.querySelector('[data-kv-grid]');
    var bulk = page.querySelector('[data-kv-bulk]');
    var selectAll = page.querySelector('[data-kv-select-all]');
    var template = page.querySelector('[data-kv-item-template]');
    var params = new URLSearchParams(window.location.search);
    var sharedParam = page.dataset.shareParam || 'items';
    var shared = params.get(sharedParam);
    var preview = shared !== null;
    var version = 0;

    page.classList.toggle('is-preview', preview);

    function handles() {
      if (!preview) return lists.wishlist.get();
      return shared.split(',').map(function (handle) {
        return handle.trim();
      }).filter(Boolean);
    }

    function buildItem(handle, fragment) {
      var item = template.content.firstElementChild.cloneNode(true);
      var slot = item.querySelector('[data-kv-card]');
      item.dataset.handle = handle;
      /*
        The card goes straight into the slot without its fragment wrapper,
        so it is the slot's flex child and takes the column width.
      */
      while (fragment.firstChild) slot.appendChild(fragment.firstChild);
      return item;
    }

    function shareUrl(list) {
      return page.dataset.shareUrl + '?' + sharedParam + '=' + encodeURIComponent(list.join(','));
    }

    function updateShare(list) {
      var url = shareUrl(list);
      var title = page.dataset.shareTitle || document.title;
      var encodedUrl = encodeURIComponent(url);
      var encodedTitle = encodeURIComponent(title);
      var targets = {
        facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + encodedUrl,
        twitter: 'https://x.com/intent/post?url=' + encodedUrl + '&text=' + encodedTitle,
        pinterest: 'https://pinterest.com/pin/create/button/?url=' + encodedUrl + '&description=' + encodedTitle,
        linkedin: 'https://www.linkedin.com/sharing/share-offsite/?url=' + encodedUrl,
        telegram: 'https://t.me/share/url?url=' + encodedUrl + '&text=' + encodedTitle,
        whatsapp: 'https://api.whatsapp.com/send?text=' + encodedTitle + '%20' + encodedUrl,
        email: 'mailto:?subject=' + encodedTitle + '&body=' + encodedUrl
      };
      Array.prototype.forEach.call(page.querySelectorAll('[data-kv-share]'), function (link) {
        var network = link.dataset.kvShare;
        if (network === 'copy') {
          link.dataset.url = url;
        } else if (targets[network]) {
          link.setAttribute('href', targets[network]);
        }
      });
    }

    function selectedItems() {
      return Array.prototype.slice.call(grid.querySelectorAll('.kv-wishlist__item.is-selected'));
    }

    function updateBulk() {
      if (!bulk) return;
      var selected = selectedItems().length;
      bulk.classList.toggle('is-visible', selected > 0);
      if (selectAll) selectAll.classList.toggle('is-selected', selected > 0 && selected === grid.children.length);
    }

    function showEmpty() {
      grid.innerHTML = '';
      setPageState(page, 'empty');
    }

    function render() {
      var current = ++version;
      var list = handles();
      if (!list.length) {
        showEmpty();
        return;
      }
      setPageState(page, 'loading');

      Promise.all(list.map(function (handle) {
        return fetchFragment(page, handle, 'card');
      })).then(function (results) {
        if (current !== version) return;
        grid.innerHTML = '';
        var missing = [];
        results.forEach(function (result) {
          if (result.missing) {
            missing.push(result.handle);
            return;
          }
          if (result.fragment) grid.appendChild(buildItem(result.handle, result.fragment));
        });
        /* A product that no longer exists is dropped so it stops being fetched. */
        if (missing.length && !preview) {
          lists.wishlist.removeMany(missing, 'wishlist-page');
        }
        if (!grid.children.length) {
          /*
            Nothing on screen. If handles are still saved then the store did
            not answer for them, which is a failure to say out loud rather
            than an empty list to announce.
          */
          if (handles().length) {
            setPageState(page, 'error');
          } else {
            showEmpty();
          }
          return;
        }
        setPageState(page, 'ready');
        updateShare(list);
        updateBulk();
        syncCards(grid);
      });
    }

    function removeItems(items) {
      var removed = items.map(function (item) {
        return item.dataset.handle;
      });
      items.forEach(function (item) {
        item.remove();
      });
      lists.wishlist.removeMany(removed, 'wishlist-page');
      if (!grid.children.length) {
        showEmpty();
      } else {
        updateShare(lists.wishlist.get());
        updateBulk();
      }
    }

    page.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-kv-remove]');
      if (remove) {
        event.preventDefault();
        var item = remove.closest('.kv-wishlist__item');
        if (item) removeItems([item]);
        return;
      }

      var bulkRemove = event.target.closest('[data-kv-bulk-remove]');
      if (bulkRemove) {
        event.preventDefault();
        var selected = selectedItems();
        if (!selected.length) return;
        if (page.dataset.confirmRemove && !window.confirm(page.dataset.confirmRemove)) return;
        removeItems(selected);
        return;
      }

      var toggleAll = event.target.closest('[data-kv-select-all]');
      if (toggleAll) {
        event.preventDefault();
        var all = !toggleAll.classList.contains('is-selected');
        Array.prototype.forEach.call(grid.querySelectorAll('.kv-wishlist__checkbox'), function (box) {
          box.checked = all;
          box.closest('.kv-wishlist__item').classList.toggle('is-selected', all);
        });
        updateBulk();
        return;
      }

      var copy = event.target.closest('[data-kv-share="copy"]');
      if (copy) {
        event.preventDefault();
        var url = copy.dataset.url || shareUrl(handles());
        var done = function () {
          copy.classList.add('is-copied');
          window.setTimeout(function () {
            copy.classList.remove('is-copied');
          }, 1500);
        };
        var fallback = function () {
          window.prompt(page.dataset.labelCopyPrompt || 'Copy this link', url);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, fallback);
        } else {
          fallback();
        }
      }
    });

    page.addEventListener('change', function (event) {
      if (!event.target.classList.contains('kv-wishlist__checkbox')) return;
      event.target.closest('.kv-wishlist__item').classList.toggle('is-selected', event.target.checked);
      updateBulk();
    });

    document.addEventListener(CHANGE_EVENT, function (event) {
      if (event.detail.list === 'wishlist' && event.detail.source !== 'wishlist-page' && !preview) render();
    });
    window.addEventListener('storage', function (event) {
      if (event.key === STORAGE.wishlist && !preview) render();
    });

    render();
  }

  /* Compare page -------------------------------------------------------- */

  function initComparePage(page) {
    var rows = Array.prototype.slice.call(page.querySelectorAll('tr[data-row]'));
    var removeTemplate = page.querySelector('[data-kv-remove-template]');
    var version = 0;

    function clearRows() {
      rows.forEach(function (row) {
        Array.prototype.forEach.call(row.querySelectorAll('td'), function (cell) {
          cell.remove();
        });
        row.hidden = false;
      });
    }

    function showEmpty() {
      clearRows();
      setPageState(page, 'empty');
    }

    /* A row that no product fills is dropped. */
    function hideEmptyRows() {
      rows.forEach(function (row) {
        if (row.dataset.row === 'basic') return;
        var cells = row.querySelectorAll('td');
        row.hidden = cells.length > 0 && Array.prototype.every.call(cells, function (cell) {
          return cell.dataset.empty === 'true';
        });
      });
    }

    /*
      A fragment is rendered with the section's own settings rather than the
      ones this page was saved with, so the stock wording is restated here.
      Only the text node is rewritten: the tick mark beside it is markup, and
      writing over the whole paragraph would take it away.
    */
    function stockLabel(cell) {
      var stock = cell.querySelector('[data-kv-stock]');
      var text = stock && stock.querySelector('[data-kv-stock-text]');
      if (!text) return;
      var count = parseInt(stock.dataset.kvQty || '0', 10);
      if (!(count > 0)) count = 0;
      var key = 'labelOutOfStock';
      if (stock.dataset.kvStock === 'in') {
        key = count > 0 ? 'labelInStockCount' : 'labelInStock';
      }
      var wording = page.dataset[key];
      if (!wording) return;
      text.textContent = count > 0 ? wording.replace('[count]', count) : wording;
    }

    function addColumn(handle, fragment) {
      rows.forEach(function (row) {
        var key = row.dataset.row;
        var source = fragment.querySelector('[data-cell="' + key + '"]');
        var cell = document.createElement('td');
        cell.className = 'kv-compare__value';
        /* The flex layout costs the table its semantics; the role gives them back. */
        cell.setAttribute('role', 'cell');
        cell.dataset.handle = handle;
        cell.dataset.title = row.querySelector('th').textContent.trim();
        if (key === 'basic' && removeTemplate) cell.appendChild(removeTemplate.content.cloneNode(true));
        if (source) {
          cell.dataset.empty = source.dataset.empty || 'false';
          while (source.firstChild) cell.appendChild(source.firstChild);
          stockLabel(cell);
        } else {
          cell.dataset.empty = 'true';
          cell.appendChild(document.createTextNode('-'));
        }
        row.appendChild(cell);
      });
    }

    function render() {
      var current = ++version;
      var list = lists.compare.get();
      if (!list.length) {
        showEmpty();
        return;
      }
      setPageState(page, 'loading');

      Promise.all(list.map(function (handle) {
        return fetchFragment(page, handle, 'column');
      })).then(function (results) {
        if (current !== version) return;
        clearRows();
        var missing = [];
        var shown = 0;
        results.forEach(function (result) {
          if (result.missing) {
            missing.push(result.handle);
            return;
          }
          if (!result.fragment) return;
          addColumn(result.handle, result.fragment);
          shown += 1;
        });
        if (missing.length) {
          lists.compare.removeMany(missing, 'compare-page');
        }
        if (!shown) {
          /* Same distinction as the wishlist: nothing saved, or nothing answered. */
          if (lists.compare.count()) {
            setPageState(page, 'error');
          } else {
            showEmpty();
          }
          return;
        }
        hideEmptyRows();
        setPageState(page, 'ready');
      });
    }

    page.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-kv-remove]');
      if (!remove) return;
      event.preventDefault();
      var cell = remove.closest('td');
      var handle = cell && cell.dataset.handle;
      if (!handle) return;

      /*
        A render already in flight would put this column back when it lands,
        so it is retired first. The handle is compared rather than spliced
        into a selector, because it comes out of storage and could hold any
        character at all.
      */
      version += 1;

      var columns = Array.prototype.filter.call(page.querySelectorAll('td[data-handle]'), function (node) {
        return node.dataset.handle === handle;
      });

      /*
        Removing the column takes the focused control with it. Focus is moved
        to the next column's remove control first, so a keyboard leaves off
        where it was rather than jumping back to the top of the page.
      */
      var successor = null;
      if (page.contains(document.activeElement)) {
        var controls = Array.prototype.slice.call(page.querySelectorAll('tr[data-row="basic"] [data-kv-remove]'));
        var index = controls.indexOf(remove);
        successor = controls[index + 1] || controls[index - 1] || null;
      }

      columns.forEach(function (node) {
        node.remove();
      });
      lists.compare.remove(handle, 'compare-page');

      if (!page.querySelector('tr[data-row="basic"] td')) {
        showEmpty();
      } else {
        hideEmptyRows();
        if (successor) successor.focus();
      }
    });

    document.addEventListener(CHANGE_EVENT, function (event) {
      if (event.detail.list === 'compare' && event.detail.source !== 'compare-page') render();
    });
    window.addEventListener('storage', function (event) {
      if (event.key === STORAGE.compare) render();
    });

    render();
  }

  /* Boot ---------------------------------------------------------------- */

  function boot() {
    updateCounts();
    syncButtons();

    var wishlistPage = document.querySelector('[data-kv-wishlist-page]');
    if (wishlistPage) initWishlistPage(wishlistPage);
    var comparePage = document.querySelector('[data-kv-compare-page]');
    if (comparePage) initComparePage(comparePage);

    /*
      Cards arrive later on some pages: filtered collections, search results
      and carousels swap their grid in. New cards are marked as soon as they
      land, and header markup swapped by a section refresh is refilled.
    */
    var WATCHED = '.kv-card, .kv-product__actions, [data-kv-wishlist-count], [data-kv-compare-count]';

    var observer = new MutationObserver(function (mutations) {
      var needsSync = false;
      for (var i = 0; i < mutations.length && !needsSync; i += 1) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          /*
            The same selectors are tested against the added node and against
            its subtree. A section refresh replaces a wrapper, so the subtree
            arm catches it; a narrower refresh could replace a counter on its
            own, and that one only the first arm sees.
          */
          if (node.matches(WATCHED) || node.querySelector(WATCHED)) {
            needsSync = true;
            break;
          }
        }
      }
      if (needsSync) {
        updateCounts();
        syncButtons();
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    window.addEventListener('storage', function (event) {
      if (event.key === STORAGE.wishlist || event.key === STORAGE.compare) {
        updateCounts();
        syncButtons();
      }
    });
  }

  window.KvLists = {
    wishlist: lists.wishlist,
    compare: lists.compare,
    pageUrl: pageUrl,
    handleFromUrl: handleFromUrl
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
